import React, { useState } from 'react';
import { 
  Play, ChevronRight, BookOpen, Layout, Calendar, 
  Hand, CreditCard, Users, Settings, CheckCircle,
  Clock, Star, ArrowLeft
} from 'lucide-react';

const tutorials = [
  {
    id: 'getting-started',
    category: 'Getting Started',
    icon: BookOpen,
    color: 'bg-emerald-500',
    items: [
      {
        id: 'welcome',
        title: 'Guided Setup Tour',
        description: 'Watch the full setup walkthrough — business details, customers, working days, payments & the email assistant',
        duration: '2 min',
        videoUrl: '/tutorials/getting-started.mp4',
        steps: [
          'Add your business name and mobile',
          'Import your customers from Squeegee or add your first one',
          'Pick your working days',
          'Connect Stripe & GoCardless to get paid',
          'Turn on the email assistant',
          'Open your Work Planner and start cleaning'
        ]
      },
      {
        id: 'first-customer',
        title: 'Adding Your First Customer',
        description: 'Learn how to add customers and their details',
        duration: '3 min',
        videoUrl: null,
        steps: [
          'Click the + button or go to Customers',
          'Fill in customer name and address',
          'Add contact details (phone, email)',
          'Save the customer'
        ]
      }
    ]
  },
  {
    id: 'dashboard',
    category: 'Dashboard',
    icon: Layout,
    color: 'bg-blue-500',
    items: [
      {
        id: 'dashboard-overview',
        title: 'Understanding Your Dashboard',
        description: 'Make sense of your daily stats and quick actions',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'View your customer count and weekly stats',
          'Check jobs scheduled for today',
          'See outstanding payments at a glance',
          'Use quick action cards to jump to sections'
        ]
      }
    ]
  },
  {
    id: 'work-planner',
    category: 'Work Planner (Schedule)',
    icon: Calendar,
    color: 'bg-purple-500',
    items: [
      {
        id: 'schedule-basics',
        title: 'Work Planner Basics',
        description: 'Navigate your daily schedule like a pro',
        duration: '3 min',
        videoUrl: null,
        steps: [
          'Swipe left/right on the date bar to change weeks',
          'Tap a day to view jobs for that day',
          'Tap a job card to see full details',
          'Use the filter to show jobs by worker'
        ]
      },
      {
        id: 'swipe-actions',
        title: 'Swipe Actions - Complete & Skip Jobs',
        description: 'The fastest way to update job status',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'Swipe RIGHT on a job card to mark as DONE',
          'Swipe LEFT on a job card to SKIP it',
          'For completed jobs, swipe either way to UNDO',
          'Tap Skip/Done buttons as an alternative'
        ]
      },
      {
        id: 'select-jobs',
        title: 'Selecting & Moving Jobs',
        description: 'Bulk actions and rescheduling made easy',
        duration: '3 min',
        videoUrl: null,
        steps: [
          'Tap the checkbox on job cards to select them',
          'Use "Select All" to select all jobs for the day',
          'Selected jobs show a green border',
          'Tap another day to move selected jobs to that day'
        ]
      },
      {
        id: 'send-reminders',
        title: 'Sending Appointment Reminders',
        description: 'Notify customers about tomorrow\'s appointments',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'Navigate to tomorrow\'s date',
          'Select specific jobs or use "Select All"',
          'Choose SMS Remind, Email Remind, or Both',
          'Customers receive reminder with appointment details'
        ]
      }
    ]
  },
  {
    id: 'jobs',
    category: 'Managing Jobs',
    icon: CheckCircle,
    color: 'bg-amber-500',
    items: [
      {
        id: 'create-job',
        title: 'Creating a New Job',
        description: 'Schedule work for your customers',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'Go to a customer profile',
          'Tap "Add Job" button',
          'Select services and enter prices',
          'Choose the date and save'
        ]
      },
      {
        id: 'complete-job',
        title: 'Completing Jobs & Invoicing',
        description: 'What happens when you mark a job done',
        duration: '3 min',
        videoUrl: null,
        steps: [
          'Swipe right or tap Done on a job',
          'Invoice is automatically created',
          'If auto-send is enabled, email goes to customer',
          'Payment link included in the invoice'
        ]
      },
      {
        id: 'undo-complete',
        title: 'Undoing a Completed Job',
        description: 'Made a mistake? No problem!',
        duration: '1 min',
        videoUrl: null,
        steps: [
          'Find the completed job (shows green tick)',
          'Swipe in either direction',
          'Confirm you want to undo',
          'Job returns to scheduled status'
        ]
      }
    ]
  },
  {
    id: 'invoicing',
    category: 'Invoicing & Payments',
    icon: CreditCard,
    color: 'bg-green-500',
    items: [
      {
        id: 'invoice-settings',
        title: 'Invoice Settings',
        description: 'Set up automatic invoicing',
        duration: '3 min',
        videoUrl: null,
        steps: [
          'Go to Settings > Invoicing',
          'Enable "Auto-send invoices" toggle',
          'Add your business details and bank info',
          'Customize invoice notes and terms'
        ]
      },
      {
        id: 'view-invoices',
        title: 'Viewing & Managing Invoices',
        description: 'Track payments and outstanding amounts',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'Go to Finances > Invoices',
          'Filter by status (Paid, Unpaid, Overdue)',
          'Click an invoice to see details',
          'Resend or mark as paid manually'
        ]
      }
    ]
  },
  {
    id: 'team',
    category: 'Team Management',
    icon: Users,
    color: 'bg-indigo-500',
    items: [
      {
        id: 'add-workers',
        title: 'Adding Team Members',
        description: 'Set up worker accounts for your team',
        duration: '3 min',
        videoUrl: null,
        steps: [
          'Go to Setup > Users & Teams',
          'Click "Add User"',
          'Enter their email and set role (Worker)',
          'Workers get a simplified view of their jobs'
        ]
      },
      {
        id: 'reassign-jobs',
        title: 'Reassigning Jobs to Workers',
        description: 'Move jobs between team members',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'Select jobs on the Work Planner',
          'Tap "Reassign Worker" button',
          'Choose the worker from the list',
          'Jobs are instantly reassigned'
        ]
      }
    ]
  },
  {
    id: 'settings',
    category: 'Settings & Setup',
    icon: Settings,
    color: 'bg-zinc-600',
    items: [
      {
        id: 'branding',
        title: 'Setting Up Your Branding',
        description: 'Add your logo and company details',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'Go to Settings > Branding',
          'Upload your company logo',
          'Set your company name',
          'Logo appears on invoices and emails'
        ]
      },
      {
        id: 'messaging-templates',
        title: 'Customizing Message Templates',
        description: 'Edit your reminder and invoice emails',
        duration: '2 min',
        videoUrl: null,
        steps: [
          'Go to Settings > Messaging',
          'Edit email reminder subject and body',
          'Customize SMS reminder text',
          'Use placeholders like {customer_name}, {service}'
        ]
      }
    ]
  }
];

const TutorialCard = ({ tutorial, onSelect }) => {
  return (
    <button
      onClick={() => onSelect(tutorial)}
      className="w-full text-left p-4 bg-white rounded-xl border border-zinc-200 hover:border-emerald-300 hover:shadow-md transition-all duration-200"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg ${tutorial.videoUrl ? 'bg-red-500' : 'bg-zinc-200'} flex items-center justify-center flex-shrink-0`}>
          <Play size={18} className="text-white ml-0.5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-zinc-900 text-sm">{tutorial.title}</h4>
          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{tutorial.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="flex items-center gap-1 text-xs text-zinc-400">
              <Clock size={12} />
              {tutorial.duration}
            </span>
            {!tutorial.videoUrl && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Coming soon</span>
            )}
          </div>
        </div>
        <ChevronRight size={18} className="text-zinc-400 flex-shrink-0" />
      </div>
    </button>
  );
};

const TutorialDetail = ({ tutorial, onBack }) => {
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-zinc-900 text-white p-4">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-white/70 hover:text-white mb-3"
        >
          <ArrowLeft size={18} />
          Back to tutorials
        </button>
        <h2 className="text-xl font-bold">{tutorial.title}</h2>
        <p className="text-white/70 text-sm mt-1">{tutorial.description}</p>
      </div>
      
      {/* Video placeholder */}
      <div className="aspect-video bg-zinc-800 flex items-center justify-center">
        {tutorial.videoUrl ? (
          <video 
            src={tutorial.videoUrl} 
            controls 
            className="w-full h-full"
            poster="/video-poster.jpg"
          />
        ) : (
          <div className="text-center text-white/50">
            <Play size={48} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Video tutorial coming soon</p>
          </div>
        )}
      </div>
      
      {/* Steps */}
      <div className="flex-1 overflow-y-auto p-4 bg-zinc-50">
        <h3 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
          <CheckCircle size={18} className="text-emerald-500" />
          Step-by-Step Guide
        </h3>
        <div className="space-y-3">
          {tutorial.steps.map((step, index) => (
            <div key={index} className="flex items-start gap-3 bg-white p-3 rounded-lg border border-zinc-200">
              <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {index + 1}
              </div>
              <p className="text-sm text-zinc-700 pt-0.5">{step}</p>
            </div>
          ))}
        </div>
        
        {/* Pro tip */}
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-1">
            <Star size={16} />
            Pro Tip
          </div>
          <p className="text-sm text-amber-800">
            Practice makes perfect! Try these steps on your account to get comfortable with the feature.
          </p>
        </div>
      </div>
    </div>
  );
};

const Learning = () => {
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState('getting-started');

  if (selectedTutorial) {
    return <TutorialDetail tutorial={selectedTutorial} onBack={() => setSelectedTutorial(null)} />;
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50">
      {/* Header */}
      <div className="bg-zinc-900 text-white p-4 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
            <BookOpen size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold">Tutorials</h1>
            <p className="text-white/60 text-sm">Video tutorials & guides</p>
          </div>
        </div>
      </div>
      
      {/* Quick tips banner */}
      <div className="mx-4 -mt-3 mb-4 p-3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl text-white shadow-lg">
        <p className="text-sm font-medium">New to StreakFree?</p>
        <p className="text-xs text-white/80 mt-0.5">Start with "Getting Started" tutorials below</p>
      </div>
      
      {/* Tutorial categories */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="space-y-3">
          {tutorials.map((category) => {
            const Icon = category.icon;
            const isExpanded = expandedCategory === category.id;
            
            return (
              <div key={category.id} className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                {/* Category header */}
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-zinc-50 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-xl ${category.color} flex items-center justify-center`}>
                    <Icon size={20} className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-zinc-900">{category.category}</h3>
                    <p className="text-xs text-zinc-500">{category.items.length} tutorial{category.items.length > 1 ? 's' : ''}</p>
                  </div>
                  <ChevronRight 
                    size={20} 
                    className={`text-zinc-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} 
                  />
                </button>
                
                {/* Tutorial items */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-2 border-t border-zinc-100 pt-3">
                    {category.items.map((tutorial) => (
                      <TutorialCard 
                        key={tutorial.id} 
                        tutorial={tutorial} 
                        onSelect={setSelectedTutorial}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Footer */}
        <div className="mt-6 text-center text-sm text-zinc-500">
          <p>More tutorials coming soon!</p>
          <p className="text-xs mt-1">Have a suggestion? Let us know</p>
        </div>
      </div>
    </div>
  );
};

export default Learning;
